import { jsPDF } from 'jspdf';
import { registerCencoraFonts, CENCORA_FONT_FAMILY } from './fontLoader';

// Cencora brand colors
const PRIMARY_COLOR = { r: 70, g: 30, b: 150 }; // #461e96
const CARD_BG_COLOR = { r: 240, g: 235, b: 247 }; // Light purple background
const TEXT_COLOR = { r: 30, g: 30, b: 30 };
const GRAY_COLOR = { r: 100, g: 100, b: 100 };

// Path to Cencora logo SVG (purple primary logo)
const CENCORA_LOGO_PATH = '/Logo/Cencora_Logo_Primary.svg';
/**
 * Loads an image and converts it to base64 for PDF embedding
 */
function loadImageAsBase64(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = src;
    });
}

/**
 * Generates and downloads a PDF for an article record based on the active tab.
 * Uses Cencora brand template design with logo and styling.
 * @param {Object} record - The article record object
 * @param {string} activeTab - The currently active tab ('summary', 'latest', or 'past')
 */
export async function generateArticlePDF(record, activeTab) {
    if (!record) {
        console.error('No record provided for PDF generation');
        return;
    }

    const doc = new jsPDF();
    
    // Register Cencora-Gilroy fonts
    registerCencoraFonts(doc);
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPosition = 20;

    // Extract record data
    const title = record.title;
    const category = record.categories?.[0];
    const country = record.country;
    const sourceName = record.sourceName;
    const sourceUrl = record.sourceUrl || record.url;
    const summary = record.aiSummary || 'No summary is available for this record.';
    const changeHistory = record.changeHistory || [];
    const latestUpdates = record.latestUpdates || [];
    const pastHistory = record.pastHistory || [];

    const scrapedText = record.scrapedDate
        ? new Date(record.scrapedDate).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        })
        : null;

    const publishedText = record.publishedDate
        ? new Date(record.publishedDate).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        })
        : null;

    // ========== HEADER: Cencora Logo (top-left corner) ==========
    try {
        const logoBase64 = await loadImageAsBase64(CENCORA_LOGO_PATH);
        doc.addImage(logoBase64, 'PNG', 10, 10, 30, 4.5);
    } catch (e) {
        console.warn('Could not load Cencora logo, using text fallback:', e);
        // Fallback: draw text logo in Cencora purple
        doc.setTextColor(PRIMARY_COLOR.r, PRIMARY_COLOR.g, PRIMARY_COLOR.b);
        doc.setFontSize(12);
        doc.setFont('CencoraGilroy', 'bold');
        doc.text('cencora', 10, 14);
    }
    yPosition = 45;

    // ========== TITLE ==========
    doc.setTextColor(PRIMARY_COLOR.r, PRIMARY_COLOR.g, PRIMARY_COLOR.b);
    doc.setFontSize(14);
    doc.setFont('CencoraGilroy', 'bold');
    if (title) {
        const titleLines = doc.splitTextToSize(title, maxWidth);
        doc.text(titleLines, margin, yPosition);
        yPosition += titleLines.length * 6 + 2;
    }

    // ========== METADATA LINES ==========
    doc.setTextColor(TEXT_COLOR.r, TEXT_COLOR.g, TEXT_COLOR.b);
    doc.setFontSize(8);
    doc.setFont('CencoraGilroy', 'normal');
    
    // First line: Source, Category, Country
    let metadataLine1 = '';
    if (sourceName) metadataLine1 += `Source: ${sourceName}`;
    if (category) {
        metadataLine1 += metadataLine1 ? '  |  ' : '';
        metadataLine1 += `Category: ${category}`;
    }
    if (country) {
        metadataLine1 += metadataLine1 ? '  |  ' : '';
        metadataLine1 += `Country: ${country}`;
    }
    
    if (metadataLine1) {
        doc.text(metadataLine1, margin, yPosition);
        yPosition += 6;
    }
    
    // Second line: Dates
    let metadataLine2 = '';
    if (scrapedText) {
        metadataLine2 += `Scraped: ${scrapedText}`;
    } 
    if (publishedText) {
        metadataLine2 += metadataLine2 ? '  |  ' : '';
        metadataLine2 += `Published: ${publishedText}`;
    }
    
    if (metadataLine2) {
        doc.text(metadataLine2, margin, yPosition);
        yPosition += 6;
    } else if (metadataLine1) {
        yPosition += 6;
    }
    
    // Third line: Source URL
    if (sourceUrl) {
        doc.setTextColor(PRIMARY_COLOR.r, PRIMARY_COLOR.g, PRIMARY_COLOR.b);
        doc.textWithLink(sourceUrl, margin, yPosition, { url: sourceUrl });
        doc.setTextColor(TEXT_COLOR.r, TEXT_COLOR.g, TEXT_COLOR.b);
        yPosition += 18;
    } else {
        yPosition += 11;
    }

    // ========== CONTENT BASED ON ACTIVE TAB ==========
    if (activeTab === 'summary') {
        yPosition = addSummaryCard(doc, summary, category, country, margin, maxWidth, yPosition, pageHeight);
    } else if (activeTab === 'latest') {
        // Use latestUpdates if available, fallback to changeHistory filtered for 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentChanges = changeHistory.filter(change => {
            const changeDate = new Date(change.changedAt);
            return changeDate >= thirtyDaysAgo;
        });
        const updatesToShow = latestUpdates.length > 0 ? latestUpdates : recentChanges;
        yPosition = addLatestUpdatesCards(doc, updatesToShow, margin, maxWidth, yPosition, pageHeight);
    } else if (activeTab === 'past') {
        yPosition = addPastUpdatesCards(doc, pastHistory, margin, maxWidth, yPosition, pageHeight);
    }

    // ========== ADD FOOTER TO ALL PAGES ==========
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(doc, i, totalPages, margin, pageWidth, pageHeight);
    }

    // Generate filename from title and tab
    const tabSuffix = activeTab === 'summary' ? 'Summary' : activeTab === 'latest' ? 'LatestUpdates' : 'PastUpdates';
    const safeTitle = title ? title.replace(/[^a-z0-9]/gi, '_').substring(0, 40) : 'Article';
    const filename = safeTitle + '_' + tabSuffix + '.pdf';
    doc.save(filename);
}

/**
 * Adds footer to the current page
 */
function addFooter(doc, currentPage, totalPages, margin, pageWidth, pageHeight) {
    const footerY = pageHeight - 15;
    
    // Footer line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    
    // Page number on the right
    doc.setTextColor(GRAY_COLOR.r, GRAY_COLOR.g, GRAY_COLOR.b);
    doc.setFontSize(8);
    doc.setFont('CencoraGilroy', 'normal');
    const pageText = `${currentPage} / ${totalPages}`;
    const pageTextWidth = doc.getTextWidth(pageText);
    doc.text(pageText, pageWidth - margin - pageTextWidth, footerY);
}

/**
 * Draws a rectangle card with light purple background (straight corners)
 */
function drawCard(doc, x, y, width, height) {
    doc.setFillColor(CARD_BG_COLOR.r, CARD_BG_COLOR.g, CARD_BG_COLOR.b);
    doc.setDrawColor(CARD_BG_COLOR.r, CARD_BG_COLOR.g, CARD_BG_COLOR.b);
    doc.rect(x, y, width, height, 'F');
}

/**
 * Adds the Summary section as a card
 */
function addSummaryCard(doc, summary, category, country, margin, maxWidth, yPosition, pageHeight) {
    const cardPadding = 12;
    const lineHeight = 6;
    
    // Calculate card height based on content
    doc.setFontSize(9);
    const contentLines = doc.splitTextToSize(summary, maxWidth - cardPadding * 2);
    let cardHeight = cardPadding * 2 + 15; // Header space
    cardHeight += contentLines.length * lineHeight + 10;
    
    // Check if card fits on page, if not add new page
    if (yPosition + cardHeight > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
    }
    
    // No background card - clean design
    
    let textY = yPosition;
    
    // Section header
    doc.setTextColor(PRIMARY_COLOR.r, PRIMARY_COLOR.g, PRIMARY_COLOR.b);
    doc.setFontSize(10);
    doc.setFont('CencoraGilroy', 'bold');
    doc.text('Summary (AI Generated)', margin, textY);
    textY += 12;
    
    // Content
    doc.setTextColor(TEXT_COLOR.r, TEXT_COLOR.g, TEXT_COLOR.b);
    doc.setFontSize(9);
    doc.setFont('CencoraGilroy', 'normal');
    contentLines.forEach((line) => {
        doc.text(line, margin, textY);
        textY += lineHeight;
    });
    
    return yPosition + cardHeight + 10;
}

/**
 * Adds the Latest Updates section (past 30 days) as cards
 */
function addLatestUpdatesCards(doc, updates, margin, maxWidth, yPosition, pageHeight) {
    // Section header
    doc.setTextColor(PRIMARY_COLOR.r, PRIMARY_COLOR.g, PRIMARY_COLOR.b);
    doc.setFontSize(11);
    doc.setFont('CencoraGilroy', 'bold');
    doc.text('Latest updates (AI generated)', margin, yPosition);
    yPosition += 12;

    if (updates.length > 0) {
        yPosition = addChangeHistoryCards(doc, updates, margin, maxWidth, yPosition, pageHeight);
    } else {
        doc.setTextColor(GRAY_COLOR.r, GRAY_COLOR.g, GRAY_COLOR.b);
        doc.setFontSize(9);
        doc.setFont('CencoraGilroy', 'normal');
        doc.text('No updates in the past 30 days.', margin, yPosition);
        yPosition += 15;
    }

    return yPosition;
}

/**
 * Adds the Past Updates section (all history) as cards
 */
function addPastUpdatesCards(doc, updates, margin, maxWidth, yPosition, pageHeight) {
    // Section header
    doc.setTextColor(PRIMARY_COLOR.r, PRIMARY_COLOR.g, PRIMARY_COLOR.b);
    doc.setFontSize(11);
    doc.setFont('CencoraGilroy', 'bold');
    doc.text('Past updates', margin, yPosition);
    yPosition += 12;

    if (updates.length > 0) {
        yPosition = addChangeHistoryCards(doc, updates, margin, maxWidth, yPosition, pageHeight);
    } else {
        doc.setTextColor(GRAY_COLOR.r, GRAY_COLOR.g, GRAY_COLOR.b);
        doc.setFontSize(9);
        doc.setFont('CencoraGilroy', 'normal');
        doc.text('No past updates available.', margin, yPosition);
        yPosition += 15;
    }

    return yPosition;
}

/**
 * Adds change history items as individual cards with page break handling
 */
function addChangeHistoryCards(doc, changes, margin, maxWidth, yPosition, pageHeight) {
    const lineHeight = 6;

    changes.forEach((change, index) => {
        // Calculate content height
        doc.setFontSize(9);
        const descLines = doc.splitTextToSize(change.changeDescription || 'No description provided.', maxWidth);
        const contentHeight = 12 + (descLines.length * lineHeight) + 8;
        
        // Check if content fits on page
        if (yPosition + contentHeight > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
        }
        
        let textY = yPosition;
        
        // Header: Date
        const changeDate = new Date(change.changedAt).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
        
        doc.setTextColor(PRIMARY_COLOR.r, PRIMARY_COLOR.g, PRIMARY_COLOR.b);
        doc.setFontSize(9);
        doc.setFont('CencoraGilroy', 'bold');
        doc.text(changeDate, margin, textY);
        
        textY += 8;
        
        // Description content
        doc.setTextColor(TEXT_COLOR.r, TEXT_COLOR.g, TEXT_COLOR.b);
        doc.setFontSize(9);
        doc.setFont('CencoraGilroy', 'normal');
        descLines.forEach((line) => {
            doc.text(line, margin, textY);
            textY += lineHeight;
        });
        
        yPosition += contentHeight;
    });

    return yPosition;
}
